import { useState } from 'react';
import { useConversation, type SavedView } from '../../contexts/ConversationContext';

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

interface SavedViewItemProps {
  view: SavedView;
  onSelect: (id: string) => void;
  onToggleStar: (id: string) => void;
  onDelete: (id: string) => void;
}

function SavedViewItem({ view, onSelect, onToggleStar, onDelete }: SavedViewItemProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showConfirm) {
      onDelete(view.id);
      setShowConfirm(false);
    } else {
      setShowConfirm(true);
      // Auto-close confirmation after 3 seconds
      setTimeout(() => setShowConfirm(false), 3000);
    }
  };

  return (
    <div
      className="group px-2 py-1.5 bg-white border border-grey-medium/20 hover:border-mint transition-colors cursor-pointer"
      onClick={() => onSelect(view.id)}
    >
      <div className="flex items-start gap-1.5">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar(view.id);
          }}
          className="mt-0.5 flex-shrink-0"
        >
          <svg
            className={`w-3 h-3 transition-colors ${
              view.starred ? 'text-mint fill-mint' : 'text-grey-medium hover:text-mint'
            }`}
            fill={view.starred ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
            />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-black font-light truncate">{view.name}</p>
          <p className="text-[10px] text-grey-medium mt-0.5 truncate">{view.query}</p>
          <p className="text-[10px] text-grey-medium/70 mt-0.5">{formatRelativeTime(view.timestamp)}</p>
        </div>
        <button
          onClick={handleDelete}
          className={`mt-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
            showConfirm ? 'text-coral' : 'text-grey-medium hover:text-coral'
          }`}
          title={showConfirm ? 'Click again to confirm' : 'Delete view'}
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            {showConfirm ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            )}
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function SavedViewsSidebar() {
  const { savedViews, loadView, deleteView, toggleViewStarred } = useConversation();

  // Sort: starred first, then by timestamp
  const sortedViews = [...savedViews].sort((a, b) => {
    if (a.starred && !b.starred) return -1;
    if (!a.starred && b.starred) return 1;
    return b.timestamp - a.timestamp;
  });

  if (savedViews.length === 0) {
    return (
      <div className="p-3">
        <p className="font-mono text-[10px] text-grey-medium uppercase tracking-wider mb-2">
          Saved Views
        </p>
        <p className="text-xs text-grey-medium font-light">
          Save visualizations for quick access later.
        </p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <p className="font-mono text-[10px] text-grey-medium uppercase tracking-wider mb-2">
        Saved Views
      </p>
      <div className="space-y-1">
        {sortedViews.map((view) => (
          <SavedViewItem
            key={view.id}
            view={view}
            onSelect={loadView}
            onToggleStar={toggleViewStarred}
            onDelete={deleteView}
          />
        ))}
      </div>
    </div>
  );
}
